import * as React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactTitle,
  DataTable,
  HelpPopover,
  PageHeader,
  SplitList,
  STICKY_TABLE_TOP,
  StatusChip,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type IDataColumn
} from '@/design';
import type { IEtagged } from '@/domains/api';
import type {
  IRefreshRunRow,
  IRefreshSchedule
} from '../../api/brokercheck-refresh-contracts';
import { brokerCheckRefreshApi } from '../../api/brokercheck-refresh-api';
import type { IPage } from '../../api/oversight-contracts';
import {
  refreshClock,
  runStateChip,
  runTriggerLabel,
  type IRefreshClock
} from '../../brokercheck-refresh';
import {
  LoadError,
  PastTheEnd,
  REFRESH_TABS,
  SectionTabs,
  useAdminData
} from '../../components/admin-page';
import { ListArea, usePaneRead } from '../../components/list-area';
import type { ICardLinkState } from '../../components/participant-link';
import { TEAM_PATHS, refreshRunPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import { Missing } from '../compliance/work-item-pane';
import { CompactTabs, useKnownFunnels, type IKnownFilter } from '../lists/work-page/light-list';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';
import {
  WorkPager,
  WORK_ITEMS_MAX_PAGE,
  dropPaging,
  readItemPages,
  usePerPage,
  useWorkPaging,
  windowOf
} from '../lists/work-page/pager';
import { sentence } from '../person-hub/said';
import { RefreshWriteHold, RunNoteLine, RunNowButton, type IRunNote } from './run-writes';
import { ZoneLine } from './schedule-page';

/**
 * The BrokerCheck refresh's Runs tab, `/team/setup/brokercheck-refresh/runs` (SR-222, F10's R6,
 * R9, Q1's (a)): survey-api's runs, newest first, one opened beside the list in the split view
 * (`AdminRefreshRunsArea`, the queue's way), and Run now in the tab's header.
 *
 * **A light work page** (SR-515, the work pages design, "Three forms"): the header row with its
 * tabs, the key number, *Refresh* handed to the app bar and the "?"; Run now the page's action;
 * Trigger and State as the columns' funnels, one value each (`trigger`, `state`), survey-api's
 * filters; the filter line; the table; the work pages' pager. No search box — a run has nothing to
 * type — no cards, no counts read. survey-api's order (newest created first), its page and its
 * total; nothing here sorts, filters or counts. A page past the end of a list that is not empty
 * says so (`PastTheEnd`), never "no runs".
 *
 * **Every instant is in the schedule's zone** (Q2, R11) — the Schedule tab's clock, named once per
 * screen. So the area reads the schedule once, for its zone and, on a paused run, for whether the
 * schedule is on (R7); the list waits for that read rather than drawing an hour that changes under
 * the reader. A schedule that cannot be read leaves the times in UTC, said with survey-api's
 * reason.
 *
 * **Nothing here polls** (R9): a running run moves at one number a second on dev, but the hub is
 * the one admin screen that polls, and an admin opens this one a few times a month. *Refresh*
 * re-reads by hand, and a write re-reads what it changed.
 */

interface IRefreshArea {
  /** The schedule's read — `null` while it is out, and after a failure. */
  schedule: Nullable<IRefreshSchedule>;
  scheduleError: Nullable<string>;
  /** The screen's clock — `null` while the schedule's read is out. */
  clock: Nullable<IRefreshClock>;
  /** Reads the schedule again, after a failure: "Read again" on either screen asks for it. */
  rereadSchedule: () => Promise<boolean>;
}

const RefreshArea = React.createContext<IRefreshArea>({
  schedule: null,
  scheduleError: null,
  clock: refreshClock('UTC'),
  rereadSchedule: async () => true
});

export function useRefreshArea(): IRefreshArea {
  return React.useContext(RefreshArea);
}

/** The runs and a run, as one area: the list, or a run's record with the list beside it. */
export function AdminRefreshRunsArea() {
  const { data, error, reload } = useAdminData<IEtagged<IRefreshSchedule>>((signal) =>
    brokerCheckRefreshApi.schedule(signal)
  );
  const schedule = data?.body ?? null;
  const area = React.useMemo<IRefreshArea>(
    () => ({
      schedule,
      scheduleError: error,
      // A failed first read draws in UTC, said; a failed later one keeps the zone last read.
      clock: schedule ? refreshClock(schedule.timeZone) : error ? refreshClock('UTC') : null,
      rereadSchedule: () => (error ? reload() : Promise.resolve(true))
    }),
    [schedule, error, reload]
  );

  return (
    <RefreshArea.Provider value={area}>
      <RefreshWriteHold>
        <ListArea
          param="runId"
          list={TEAM_PATHS.refreshRuns}
          paneTitle="Runs"
          pane={(search, selected) => <RunsListPane search={search} selected={selected} />}
        />
      </RefreshWriteHold>
    </RefreshArea.Provider>
  );
}

/** The zone the screen's times are in, said once — or that they are in UTC for want of the
 *  schedule. Nothing while the schedule is out: nothing is drawn in a zone yet. */
export function RunsZoneLine() {
  const { schedule, scheduleError, clock } = useRefreshArea();
  if (schedule && clock) return <ZoneLine clock={clock} stored={schedule.timeZone} />;
  if (scheduleError === null) return null;
  return (
    <p className="text-[0.8125rem] text-muted-foreground" data-time-zone="UTC">
      Times are in UTC — the schedule, whose time zone they are drawn in, could not be read.{' '}
      {sentence(scheduleError)}
    </p>
  );
}

/** The runs' filters: what started a run and where it stands, survey-api's vocabularies. */
const RUN_FILTERS: readonly IKnownFilter[] = [
  {
    param: 'trigger',
    column: 'trigger',
    label: 'Trigger',
    options: ['scheduled', 'manual'].map((value) => ({ value, label: runTriggerLabel(value) })),
    labelOf: runTriggerLabel
  },
  {
    param: 'state',
    column: 'state',
    label: 'State',
    options: ['running', 'paused', 'completed', 'cancelled'].map((value) => ({
      value,
      label: runStateChip(value).label
    })),
    labelOf: (state) => runStateChip(state).label
  }
];

/** One page of `pageSize` runs under the address's filters. */
function readRuns(
  params: URLSearchParams,
  page: number,
  pageSize: number,
  signal: AbortSignal
): Promise<IPage<IRefreshRunRow>> {
  return brokerCheckRefreshApi.runs(
    {
      page,
      pageSize,
      state: params.get('state') || undefined,
      trigger: params.get('trigger') || undefined
    },
    signal
  );
}

/** The window the address names, in the viewer's Per page: one read where it can be. */
function readRunsWindow(
  params: URLSearchParams,
  page: number,
  perPage: number,
  pages: number,
  signal: AbortSignal
): Promise<IPage<IRefreshRunRow>> {
  return readItemPages(
    (at, size) => readRuns(params, at, size, signal),
    page,
    perPage,
    pages,
    WORK_ITEMS_MAX_PAGE
  );
}

export function AdminRefreshRunsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { clock, rereadSchedule } = useRefreshArea();
  const [note, setNote] = React.useState<Nullable<IRunNote>>(null);
  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const state = params.get('state') || null;
  const trigger = params.get('trigger') || null;
  const asked = JSON.stringify([state, trigger, page, perPage, pages]);

  const { data, error, reading, reload } = useAdminData<{
    asked: string;
    filtered: boolean;
    runs: IPage<IRefreshRunRow>;
  }>(
    async (signal) => {
      try {
        return {
          asked,
          filtered: state !== null || trigger !== null,
          runs: await readRunsWindow(params, page, perPage, pages, signal)
        };
      } catch (listError) {
        throw new Error(refusalMessage(listError, 'Could not read the runs'));
      }
    },
    [asked]
  );
  // The last answer while the next is out; nothing once this query's read has failed.
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  const readAt = useReadAt(data);
  const readAgain = () => {
    void rereadSchedule();
    return reload();
  };
  /** A change of filter starts at the top. */
  const choose = (name: string, value: Nullable<string>) =>
    setParams((current) => {
      const next = dropPaging(new URLSearchParams(current));
      if (value === null) next.delete(name);
      else next.set(name, value);
      return next;
    });
  const clearAll = () =>
    setParams((current) => {
      const next = dropPaging(new URLSearchParams(current));
      for (const filter of RUN_FILTERS) next.delete(filter.param);
      return next;
    });
  const funnels = useKnownFunnels(RUN_FILTERS, params, choose);
  const openRun = (run: IRefreshRunRow) =>
    navigate(refreshRunPath(run.id), {
      state: { search: location.search, list: true } satisfies ICardLinkState
    });

  const tableState: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : clock === null
        ? { kind: 'loading' }
        : shown.runs.items.length > 0
          ? undefined
          : {
              kind: 'empty',
              content:
                shown.runs.total > 0 ? (
                  <PastTheEnd
                    page={shown.runs.page}
                    pageSize={shown.runs.pageSize}
                    total={shown.runs.total}
                    counted={['run', 'runs']}
                    onPage={paging.setPage}
                  />
                ) : shown.filtered ? (
                  'No run matches these filters.'
                ) : (
                  'No run yet — no slot has started one, and nobody has pressed Run now.'
                )
            };

  const total = shown?.asked === asked ? shown.runs.total : null;
  const refresh = { reading, readAt, onClick: () => void readAgain() };
  const help = (
    <HelpPopover label="About the runs" align="end">
      <p>
        Every BrokerCheck refresh run, newest first: a slot of the schedule starts one, and Run now
        starts one at once. A run asks FINRA&apos;s BrokerCheck about every CRD in its scope, one a
        second; Progress is how many it has asked about. Open a run for what it found and the work
        items it raised.
      </p>
      <p>
        The Trigger and State columns&apos; funnels narrow the list to one value each. Every time is
        in the schedule&apos;s time zone.
      </p>
    </HelpPopover>
  );
  const runNow = <RunNowButton onStarted={openRun} onSaid={setNote} reread={reload} />;
  const toolbar = useWorkToolbar();
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);
  const chips = funnels.chips;

  return (
    <>
      <PageHeader
        title="BrokerCheck refresh"
        documentTitle="BrokerCheck refresh runs"
        row
        tabs={<SectionTabs tabs={REFRESH_TABS} />}
        actions={
          <WorkPageHeaderActions
            summary={total === null ? null : `${total.toLocaleString('en-US')} ${total === 1 ? 'run' : 'runs'}`}
            summaryData={{ 'data-runs-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="BrokerCheck refresh runs, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={chips.length > 0 && <CompactChipLine chips={chips} onClearAll={clearAll} />}
      >
        <CompactTitle>BrokerCheck refresh</CompactTitle>
        <CompactTabs label="Section" tabs={REFRESH_TABS} />
        <span className="flex-1" />
        <CompactDivider />
        {runNow}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        <WorkToolbar
          toolbar={toolbar}
          status={{
            reading,
            count: shown ? `${shown.runs.total.toLocaleString('en-US')} found` : undefined
          }}
          lead={<RunsZoneLine />}
          quick={null}
          quickCount={0}
          end={runNow}
        />
        {chips.length > 0 && <CompactChipLine label="Filters" chips={chips} onClearAll={clearAll} />}
        <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />
        {note && <RunNoteLine note={note} search={location.search} />}
        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the runs as last read. ${error}`} />
        )}

        <DataTable
          label="Runs"
          columns={clock ? runColumns(clock, funnels.header) : []}
          rows={tableState ? null : (shown?.runs.items ?? null)}
          rowKey={(run) => run.id}
          rowProps={(run) => ({ 'data-run': run.id }) as React.HTMLAttributes<HTMLTableRowElement>}
          onOpen={openRun}
          busy={reading && shown !== null}
          state={tableState}
          minWidth="56rem"
          stickyTop={STICKY_TABLE_TOP}
        />

        {shown && <WorkPager {...paging.pager} total={shown.runs.total} busy={reading} sticky />}
      </div>
    </>
  );
}

/** A run's state as its chip — `[data-run-state]` is what a spec reads. */
export function RunStateChip({ state }: { state: string }) {
  const chip = runStateChip(state);
  return (
    <span data-run-state={state}>
      <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
    </span>
  );
}

/** What started a run: a slot's, with the slot, or an admin's Run now, with who pressed it. */
export function triggerDetail(run: IRefreshRunRow, clock: IRefreshClock): Nullable<string> {
  if (run.trigger === 'scheduled') {
    return run.slot === null ? null : `Slot ${clock.instant(run.slot)}`;
  }
  if (run.trigger === 'manual') return run.requestedBy?.email ?? null;
  return null;
}

/**
 * How far a run has got — a bar and "`processed` of `inScope`", the counts as sent. A run with
 * nothing in scope says so rather than drawing an empty bar, which would read as a run not begun.
 */
export function RunProgress({ run }: { run: IRefreshRunRow }) {
  if (run.inScope === 0) {
    return <span className="text-sm whitespace-nowrap">Nothing in scope</span>;
  }
  // The width is clamped; the words are the counts as sent.
  const share = Math.min(1, run.processed / run.inScope);
  return (
    <span className="grid min-w-24 gap-1">
      <span
        role="progressbar"
        aria-label="Processed"
        aria-valuemin={0}
        aria-valuemax={run.inScope}
        aria-valuenow={Math.min(run.processed, run.inScope)}
        className="h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <span
          className="block h-full rounded-full bg-[var(--color-sapphire)]"
          style={{ width: `${share * 100}%` }}
        />
      </span>
      <span className="text-sm whitespace-nowrap">
        {run.processed} of {run.inScope}
      </span>
    </span>
  );
}

function runColumns(
  clock: IRefreshClock,
  head: (filter: IKnownFilter) => React.ReactNode
): IDataColumn<IRefreshRunRow>[] {
  return [
    {
      id: 'trigger',
      header: head(RUN_FILTERS[0]),
      label: 'Trigger',
      className: 'font-medium',
      cell: (run) => {
        const detail = triggerDetail(run, clock);
        return (
          <>
            {runTriggerLabel(run.trigger)}
            {detail && (
              <span className="block text-xs font-normal text-muted-foreground">{detail}</span>
            )}
          </>
        );
      }
    },
    {
      id: 'state',
      header: head(RUN_FILTERS[1]),
      label: 'State',
      cell: (run) => <RunStateChip state={run.state} />
    },
    { id: 'progress', header: 'Progress', cell: (run) => <RunProgress run={run} /> },
    {
      id: 'created',
      header: 'Created',
      className: 'text-xs whitespace-nowrap',
      cell: (run) => clock.instant(run.createdAt)
    },
    {
      id: 'started',
      header: 'Started',
      className: 'text-xs whitespace-nowrap',
      cell: (run) =>
        run.startedAt === null ? (
          <span className="text-muted-foreground">Not started</span>
        ) : (
          clock.instant(run.startedAt)
        )
    },
    {
      id: 'finished',
      header: 'Finished',
      className: 'text-xs whitespace-nowrap',
      cell: (run) => (run.finishedAt === null ? <Missing /> : clock.instant(run.finishedAt))
    }
  ];
}

/** The runs as the pane beside a run draws them: what started it and when, how far, its state. */
function RunsListPane({ search, selected }: { search: string; selected: string }) {
  const { clock } = useRefreshArea();
  const [perPage] = usePerPage();
  const { shown, pager, status } = usePaneRead(search, 'No run yet.', (params, page, signal) => {
    const window = windowOf(params, perPage);
    return readRunsWindow(params, page, perPage, page === window.page ? window.pages : 1, signal);
  });
  return (
    <SplitList
      label="Runs"
      selected={selected}
      items={
        clock === null
          ? null
          : (shown?.items.map((run) => ({
              key: run.id,
              to: refreshRunPath(run.id),
              state: { search, list: true } satisfies ICardLinkState,
              primary: `${runTriggerLabel(run.trigger)} · ${clock.instant(run.createdAt)}`,
              secondary:
                run.inScope === 0 ? 'Nothing in scope' : `${run.processed} of ${run.inScope}`,
              trailing: <RunStateChip state={run.state} />
            })) ?? null)
      }
      status={status}
      footer={pager}
    />
  );
}
